import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import './Dashboard.css';
import SalesChart from '../../components/SalesChart';
import { FaDollarSign } from 'react-icons/fa';
import { TfiShoppingCart } from 'react-icons/tfi';
import { HiCube } from 'react-icons/hi2';
import { MdOutlineShieldMoon, MdOutlineDashboard } from 'react-icons/md';
import axiosInstance from '../../utils/axiosInstance';
import { getStoredUser, isSuperAdmin, canAccessPage } from '../../utils/permissions';
import { useCurrency } from '../../context/CurrencyContext';

// ---------- helpers ----------
const toList = (data, key) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  if (data && key && Array.isArray(data[key])) return data[key];
  return [];
};

const orderShopId = (o) =>
  o.shop_id || o.shop_details?.shop_id || o.shop?.shop_id || o.shop?.id || null;

const orderShopName = (o) =>
  o.shop_details?.shop_name || o.shop_name || o.shop?.shop_name || o.shop?.name || '';

const relativeTime = (value) => {
  if (!value) return 'Recently joined';
  const hours = Math.floor((Date.now() - new Date(value)) / 36e5);
  if (hours < 1) return 'Joined just now';
  if (hours < 24) return `Joined ${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `Joined ${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  return `Joined ${months} month${months === 1 ? '' : 's'} ago`;
};

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  return isNaN(d) ? '-' : d.toISOString().split('T')[0];
};

const statusClass = (s) => (s ? String(s).replace(/\s/g, '-').toLowerCase() : '');

// this month vs last month, based on order date
const monthTrend = (orders, pick) => {
  const now = new Date();
  const cur = new Date(now.getFullYear(), now.getMonth(), 1);
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  let a = 0;
  let b = 0;
  orders.forEach((o) => {
    const d = new Date(o.order_Placment_Date);
    if (isNaN(d)) return;
    const first = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
    if (first === cur.getTime()) a += pick(o);
    else if (first === prev.getTime()) b += pick(o);
  });
  if (b === 0) return a > 0 ? 'New activity this month' : 'No activity last month';
  const pct = ((a - b) / b) * 100;
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}% vs last month`;
};

const Dashboard = () => {
  const location = useLocation();
  const user = getStoredUser();
  const superAdmin = isSuperAdmin(user);

  const {
    currency,
    formatPrice,
    getCurrencySymbol,
    getCurrentCurrency,
    shopChangeKey,
    getRateBetween,
  } = useCurrency();

  // What this account may see (avoids requests that would return 403)
  const canOrders = canAccessPage(user, 'orders');
  const canProducts = canAccessPage(user, 'products');
  const canCustomers = canAccessPage(user, 'customers');

  const selectedShopId = useMemo(() => {
    const match = location.pathname.match(/\/shop\/(\d+)/);
    if (match) return match[1];
    return !superAdmin && user?.shop_id ? String(user.shop_id) : 'all';
  }, [location.pathname, superAdmin, user]);

  const [shops, setShops] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(canOrders);
  const [productsLoading, setProductsLoading] = useState(canProducts);
  const [customersLoading, setCustomersLoading] = useState(canCustomers);
  const [errors, setErrors] = useState({});

  const setError = (key, value) => setErrors((p) => ({ ...p, [key]: value }));
  const failMsg = (e) =>
    e?.response?.status === 403 ? 'You do not have access to this data.' : 'Could not load data.';

  // Shops (Super Admin only - staff already know their own shop)
  useEffect(() => {
    if (!superAdmin) return;
    axiosInstance
      .get('/user/shops/')
      .then((res) => setShops(toList(res.data, 'shops')))
      .catch((e) => console.error('Dashboard - shops error:', e));
  }, [superAdmin, shopChangeKey]);

  // Orders
  useEffect(() => {
    if (!canOrders) return;
    setOrdersLoading(true);
    axiosInstance
      .get('/order/getAllorder/')
      .then((res) => {
        setAllOrders(toList(res.data, 'orders'));
        setError('orders', '');
      })
      .catch((e) => {
        console.error('Dashboard - orders error:', e);
        setError('orders', failMsg(e));
      })
      .finally(() => setOrdersLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canOrders, shopChangeKey]);

  // Products + customers (shop specific)
  useEffect(() => {
    const params = selectedShopId !== 'all' ? { shop_id: selectedShopId } : {};

    if (canProducts) {
      setProductsLoading(true);
      axiosInstance
        .get('/api/product/products/', { params })
        .then((res) => {
          setAllProducts(toList(res.data, 'products'));
          setError('products', '');
        })
        .catch((e) => {
          console.error('Dashboard - products error:', e);
          setError('products', failMsg(e));
        })
        .finally(() => setProductsLoading(false));
    }

    if (canCustomers) {
      setCustomersLoading(true);
      axiosInstance
        .get('/user/getAllcustomers/', { params })
        .then((res) => {
          const list = toList(res.data, 'customers').map((c) => ({
            id: c.id,
            name: `${c.first_Name || ''} ${c.last_Name || ''}`.trim(),
            email: c.email,
            initials: `${(c.first_Name?.[0] || '').toUpperCase()}${(c.last_Name?.[0] || '').toUpperCase()}`,
            createdAt: c.created_at,
            joined: relativeTime(c.created_at),
          }));
          list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
          setCustomers(list);
          setError('customers', '');
        })
        .catch((e) => {
          console.error('Dashboard - customers error:', e);
          setError('customers', failMsg(e));
        })
        .finally(() => setCustomersLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShopId, shopChangeKey, canProducts, canCustomers]);

  // ---------- derived data ----------
  const shopName = useMemo(() => {
    if (selectedShopId === 'all') return '';
    if (!superAdmin) return user?.shop_name || '';
    const s = shops.find((x) => String(x.shop_id || x.id) === String(selectedShopId));
    return s ? s.shop_name || s.name || '' : '';
  }, [selectedShopId, shops, superAdmin, user]);

  const orders = useMemo(
    () =>
      selectedShopId === 'all'
        ? allOrders
        : allOrders.filter((o) => String(orderShopId(o)) === String(selectedShopId)),
    [allOrders, selectedShopId]
  );

  const latestOrders = useMemo(
    () =>
      [...orders]
        .sort((a, b) => new Date(b.order_Placment_Date) - new Date(a.order_Placment_Date))
        .slice(0, 5),
    [orders]
  );

  const totalSales = useMemo(
    () => orders.reduce((s, o) => s + (Number(o.total_Amount) || 0), 0),
    [orders]
  );

  const stockValue = useMemo(
    () =>
      allProducts.reduce(
        (s, p) => s + (parseFloat(p.product_Price) || 0) * (parseInt(p.product_Stock, 10) || 0),
        0
      ),
    [allProducts]
  );

  const salesChartData = useMemo(() => {
    const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const totals = new Array(12).fill(0);
    orders.forEach((o) => {
      const d = new Date(o.order_Placment_Date);
      if (!isNaN(d)) totals[d.getMonth()] += Number(o.total_Amount) || 0;
    });
    return names.map((name, i) => ({ name, sales: totals[i] }));
  }, [orders]);

  const statusCounts = useMemo(() => {
    const map = {};
    orders.forEach((o) => {
      const s = o.order_Status || 'Unknown';
      map[s] = (map[s] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [orders]);

  // Super Admin, "all shops": orders and sales per shop
  const shopSummary = useMemo(() => {
    const map = {};
    allOrders.forEach((o) => {
      const id = orderShopId(o) || 'x';
      if (!map[id]) {
        const found = shops.find((s) => String(s.shop_id || s.id) === String(id));
        map[id] = {
          id,
          name: found?.shop_name || found?.name || orderShopName(o) || 'Unknown shop',
          orders: 0,
          sales: 0,
        };
      }
      map[id].orders += 1;
      map[id].sales += Number(o.total_Amount) || 0;
    });
    return Object.values(map).sort((a, b) => b.sales - a.sales).slice(0, 8);
  }, [allOrders, shops]);

  const fmt = (v) => (v === undefined || v === null ? `${getCurrencySymbol()}0.00` : formatPrice(v));
  const rate = getRateBetween ? Number(getRateBetween(currency, 'PKR')) || 0 : 0;
  const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const suffix = selectedShopId !== 'all' && shopName ? ` - ${shopName}` : '';
  const where = selectedShopId !== 'all' ? shopName || 'this shop' : 'all shops';

  const kpis = [
    canOrders && { icon: <TfiShoppingCart />, title: 'Total Orders', value: ordersLoading ? '...' : orders.length.toLocaleString(), sub: monthTrend(orders, () => 1) },
    canOrders && { icon: <FaDollarSign />, title: 'Total Sales', value: ordersLoading ? '...' : fmt(totalSales), sub: monthTrend(orders, (o) => Number(o.total_Amount) || 0) },
    canProducts && { icon: <HiCube />, title: 'Total Products', value: productsLoading ? '...' : allProducts.length.toLocaleString(), sub: `Products in ${where}` },
    canProducts && { icon: <MdOutlineShieldMoon />, title: 'Total Stock Value', value: productsLoading ? '...' : fmt(stockValue), sub: `Stock value in ${where}` },
  ].filter(Boolean);

  const stateRow = (loading, error, empty, text) =>
    loading ? <div className="dash-state">Loading...</div>
      : error ? <div className="dash-state err">{error}</div>
      : empty ? <div className="dash-state">{text}</div> : null;

  return (
    <div className="dashboard">
      <header className="dash-header">
        <h1><MdOutlineDashboard /> Dashboard{suffix}</h1>
        <div className="dash-chips">
          <span className="dash-chip">
            {getCurrentCurrency()} ({getCurrencySymbol()})
            {rate > 0 && <em>1 {getCurrentCurrency()} = {rate.toFixed(2)} PKR</em>}
          </span>
          <span className="dash-chip">{today}</span>
        </div>
      </header>

      {!canOrders && !canProducts && !canCustomers && (
        <div className="dash-card"><div className="dash-state">No dashboard sections are available for your account.</div></div>
      )}

      {kpis.length > 0 && (
        <div className="dash-kpis">
          {kpis.map((k) => (
            <div className="dash-kpi" key={k.title}>
              <div className="dash-kpi-icon">{k.icon}</div>
              <div>
                <div className="dash-kpi-title">{k.title}</div>
                <div className="dash-kpi-value">{k.value}</div>
                <div className="dash-kpi-sub">{k.sub}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {canOrders && (
        <div className="dash-row wide">
          <section className="dash-card">
            <div className="dash-card-head">Sales Analytics</div>
            <div className="dash-card-body">
              <SalesChart data={salesChartData} currencySymbol={getCurrencySymbol()} />
            </div>
          </section>

          <section className="dash-card">
            <div className="dash-card-head">Orders by Status</div>
            <div className="dash-card-body">
              {stateRow(ordersLoading, errors.orders, statusCounts.length === 0, 'No orders found') ||
                statusCounts.map(([name, count]) => (
                  <div className="dash-status" key={name}>
                    <span className="dash-status-name">{name}</span>
                    <span className="dash-bar"><i style={{ width: `${(count / orders.length) * 100}%` }} /></span>
                    <b>{count}</b>
                  </div>
                ))}
            </div>
          </section>
        </div>
      )}

      {(canCustomers || canOrders) && (
        <div className="dash-row">
          {canOrders && (
            <section className="dash-card">
              <div className="dash-card-head">Latest Orders{suffix}</div>
              {stateRow(ordersLoading, errors.orders, latestOrders.length === 0, `No orders found for ${where}`) || (
                <div className="dash-scroll">
                  <table className="dash-table">
                    <thead>
                      <tr><th>Order ID</th><th>Date</th><th>Customer</th><th>Amount</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                      {latestOrders.map((o, i) => (
                        <tr key={o.order_Id || i}>
                          <td className="link">{o.order_Tracking_Id || '-'}</td>
                          <td>{formatDate(o.order_Placment_Date)}</td>
                          <td>{`${o.customer?.first_Name || o.customer?.firstName || ''} ${o.customer?.last_Name || o.customer?.lastName || ''}`.trim() || '-'}</td>
                          <td>{fmt(o.total_Amount)}</td>
                          <td><span className={`pill ${statusClass(o.order_Status)}`}>{o.order_Status || '-'}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          {canCustomers && (
            <section className="dash-card">
              <div className="dash-card-head">New Join Customers{suffix}</div>
              {stateRow(customersLoading, errors.customers, customers.length === 0, `No customers found for ${where}`) || (
                <div className="dash-scroll">
                  <table className="dash-table">
                    <thead><tr><th>Customer</th><th>Joined</th></tr></thead>
                    <tbody>
                      {customers.slice(0, 5).map((c, i) => (
                        <tr key={c.id || i}>
                          <td>
                            <div className="dash-cust">
                              <span className="dash-avatar">{c.initials}</span>
                              <span><b className="link">{c.name || '-'}</b><small>{c.email}</small></span>
                            </div>
                          </td>
                          <td><span className="pill">{c.joined}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </div>
      )}

      {superAdmin && canOrders && selectedShopId === 'all' && shopSummary.length > 0 && (
        <section className="dash-card">
          <div className="dash-card-head">Shop Summary</div>
          <div className="dash-scroll">
            <table className="dash-table">
              <thead><tr><th>Shop</th><th>Orders</th><th>Sales</th></tr></thead>
              <tbody>
                {shopSummary.map((s) => (
                  <tr key={s.id}>
                    <td className="link">{s.name}</td>
                    <td>{s.orders.toLocaleString()}</td>
                    <td>{fmt(s.sales)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
};

export default Dashboard;